import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HsHypersceneViewer, HsViewerInput, type HsBoundingBoxView } from '@sibe/hyperscene';
import { HsViewerApi } from '@sibe/hyperscene/api/viewer';
import { HsFrustumFormat } from '@sibe/hyperscene/formats/frustum';
import { HsMaterialsFormat } from '@sibe/hyperscene/formats/materials';
import {
  HsSceneSource,
  hsSceneSourceApi,
  type HsSceneRefreshProgress,
  type HsSceneRefreshResult,
} from '@sibe/hyperscene/sources/scene';
import { HsHttpTransport } from '@sibe/hyperscene/transports/http';
import { KEY_MAP } from '../hyperscene-new/utils/key-map.global';

interface RuntimeConfig {
  apiBaseUrl?: string;
}

/**
 * The smallest host of the SDK viewer: a canvas, camera input and a scene source. It is the reference for
 * `dataroom/FRONTEND_SDK_INTEGRATION_GUIDE.md`; keep it free of demo-only state.
 */
@Component({
  selector: 'sdk-viewer',
  templateUrl: './sdk-viewer.component.html',
  styleUrls: ['./sdk-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdkViewerDemo implements AfterViewInit, OnDestroy {
  public sceneId: string = '';
  public status: string = 'Enter a scene id.';
  public error: string | null = null;
  /** Node under the last click, or null. */
  public selectedNodeId: string | null = null;

  @ViewChild('canvas', { static: true })
  private readonly canvas: ElementRef<HTMLCanvasElement> | null = null;
  private viewer: HsHypersceneViewer | null = null;
  private input: HsViewerInput | null = null;
  private source: HsSceneSource | null = null;
  private api: HsViewerApi | null = null;
  private openRequest: number = 0;
  private pointerDownAt: [number, number] | null = null;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  public ngAfterViewInit(): void {
    const canvas: HTMLCanvasElement | undefined = this.canvas?.nativeElement;
    if (canvas === undefined) {
      return;
    }
    // The viewer draws on animation frames; keep them out of Angular change detection.
    this.zone.runOutsideAngular(() => this.createViewer(canvas));
    const sceneId: string | null = this.route.snapshot.queryParamMap.get('sceneId');
    if (sceneId !== null && sceneId.length > 0) {
      void this.open(sceneId);
    }
  }

  public ngOnDestroy(): void {
    this.input?.dispose();
    this.source?.dispose();
    this.viewer?.dispose();
  }

  public async open(sceneId: string): Promise<void> {
    const source: HsSceneSource | null = this.source;
    const api: HsViewerApi | null = this.api;
    const viewer: HsHypersceneViewer | null = this.viewer;
    if (source === null || api === null || viewer === null || sceneId.trim().length === 0) {
      return;
    }
    this.sceneId = sceneId.trim();
    this.error = null;
    this.setStatus('Opening the scene...');
    void this.router.navigate([], { queryParams: { sceneId: this.sceneId }, replaceUrl: true });
    // A later open wins: an earlier, slower one must not move the camera afterwards.
    const request: number = ++this.openRequest;
    try {
      const box: HsBoundingBoxView | null = await api.getSceneBoundingBox(this.sceneId);
      if (request !== this.openRequest) {
        return;
      }
      if (box !== null) {
        viewer.frameBox(box);
      }
      await source.open(this.sceneId);
    } catch (error: unknown) {
      this.showError(error);
    }
  }

  public onCanvasPointerDown(event: PointerEvent): void {
    this.pointerDownAt = [event.clientX, event.clientY];
  }

  /** Selects the node under a click; drags that moved the camera do not select. */
  public onCanvasClick(event: MouseEvent): void {
    const start: [number, number] | null = this.pointerDownAt;
    if (start !== null && Math.hypot(event.clientX - start[0], event.clientY - start[1]) > 4) {
      return;
    }
    this.selectedNodeId = this.source?.pick(event.clientX, event.clientY)?.nodeId ?? null;
  }

  public onSceneInput(event: Event): void {
    this.sceneId = (event.target as HTMLInputElement).value;
  }

  private createViewer(canvas: HTMLCanvasElement): void {
    const viewer: HsHypersceneViewer = new HsHypersceneViewer({ canvas });
    this.viewer = viewer;
    this.input = new HsViewerInput(viewer, { keyMap: KEY_MAP });

    viewer.use(new HsFrustumFormat());
    viewer.use(new HsMaterialsFormat());
    const api: HsViewerApi = new HsViewerApi({
      transport: new HsHttpTransport({ baseUrl: resolveApiBaseUrl() }),
      formats: viewer.plugins.formats,
    });
    this.api = api;

    const source: HsSceneSource = viewer.use(
      new HsSceneSource({
        api: hsSceneSourceApi(api),
        decodePool: {
          createWorker: () => new Worker(new URL('./geometry-decode.worker.ts', import.meta.url), { type: 'classic' }),
        },
      })
    );
    this.source = source;
    source.on('progress', (progress: HsSceneRefreshProgress) =>
      this.setStatus(`${progress.message} ${progress.stats.meshCount} meshes drawn.`)
    );
    source.on('refreshed', (result: HsSceneRefreshResult) =>
      this.setStatus(
        `${result.stats.meshCount} meshes, ${result.stats.lineCount} lines, ` +
          `${result.stats.pointCloudCount} point cloud blocks.`
      )
    );
    source.on('error', (error: unknown) => this.showError(error));
  }

  private setStatus(status: string): void {
    this.zone.run(() => {
      this.status = status;
      this.changeDetector.markForCheck();
    });
  }

  private showError(error: unknown): void {
    this.zone.run(() => {
      this.error = error instanceof Error ? error.message : String(error);
      this.changeDetector.markForCheck();
    });
  }
}

/** The deployed demo serves its API from `api.<host>`; local development uses port 5000. */
function resolveApiBaseUrl(): string {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const config: RuntimeConfig | undefined = (globalThis as { __HYPERSCENE_RUNTIME_CONFIG__?: RuntimeConfig })
    .__HYPERSCENE_RUNTIME_CONFIG__;
  const configured: string | undefined = config?.apiBaseUrl?.trim();
  if (configured !== undefined && configured.length > 0) {
    return configured;
  }
  const host: string = globalThis.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'http://localhost:5000';
  }
  return `${globalThis.location.protocol}//api.${globalThis.location.hostname}`;
}
